import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { UploadChangeParam } from 'antd/lib/upload/interface';
import { CancelTokenSource } from 'axios';
import Configs from 'config';
import { get, isEmpty } from 'lodash';
import { RootState } from 'modules';
import EditorComponent from 'components/base/EditorComponent';
import NoMatch from 'components/base/error/NoMatch';
import apiClient from 'libs/api/apiClient';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import { usePermission } from 'libs/hooks';
import { filteredFileNames } from 'libs/utils';
import { resetState } from 'modules/framework/editFramework';
import { TLearningBoard } from 'modules/learning/boardTypes';
import { CUploadFileProps } from 'constants/base.const';
import { CWindowWidth, CDefaultEditorHeight } from 'constants/base.const';
import { EOperation, ENotificationType } from 'enums/base.enum';
import { Button, Form, Input, Upload, Divider, Row, Col, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { device, PrimaryColor } from 'GlobalStyles';
import EditorHeader from './EditorHeader';

const LearningBoardEditor = ({ pathName, operation, record }: TLearningBoard) => {
  const { adminPermission } = usePermission();
  const history = useHistory();

  const createBoard = (params: any, cancel?: CancelTokenSource) => {
    return apiClient.post(`/drill/board`, params, {
      cancelToken: cancel?.token,
    });
  };

  const updateBoard = (id?: string, params?: any, cancel?: CancelTokenSource) => {
    return apiClient.put(`/drill/board/${id}`, params, {
      cancelToken: cancel?.token,
    });
  };

  const BoardEdit = ({ isEdit }: { isEdit?: boolean }) => {
    const [form] = Form.useForm();
    const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
    const modalInfo = useSelector((store: RootState) => store.getFrameworkData);
    const dispatch = useDispatch();

    useEffect(() => {
      if (isEmpty(record) === false) {
        form.setFieldsValue({
          subject: get(record, 'subject'),
          subcategory: get(record, 'subcategory.id'),
          title: get(record, 'title'),
          subtitle: get(record, 'subtitle'),
          is_example: get(record, 'is_example'),
          exerciseBtn: get(record, 'exercise_button'),
          mentoring_field: get(record, 'mentoring_field'),
          body: get(record, 'body_list[0].body'),
          bottomBody: get(record, 'body_list[1].body'),
          images:
            get(record, 'thumbnail.id', 0) > 0
              ? [
                  {
                    uid: get(record, 'thumbnail.id'),
                    status: 'done',
                    name: 'thumbnail',
                  },
                ]
              : null,
        });
      }

      return () => {
        dispatch(resetState());
      };
    }, []);

    function onClickSubmit() {
      form
        .validateFields()
        .then((value) => {
          handleUpdate(value);
        })
        .catch((errorInfo) => {
          message.error('필수 사항들을 입력해주세요.');
          moveToFirstError(form, errorInfo.errorFields);
        });
      async function handleUpdate(value: any) {
        try {
          setSubmitButtonDisabled(true);
          let data;
          if (!modalInfo.subject) {
            data = {
              board_number: get(record, 'board_number'),
              subject: get(record, 'subject'),
              subcategory_id: get(record, 'subcategory.id'),
              title: get(value, 'title'),
              subtitle: get(record, 'subtitle'),
              is_example: get(record, 'is_example'),
              exercise_button: get(record, 'exercise_button'),
              mentoring_field: get(record, 'mentoring_field'),
              board_body: [
                {
                  id: get(record, 'body_list[0].id'),
                  body: get(value, 'body'),
                },
              ],
              file_ids: filteredFileNames(get(value, 'files')),
              thumbnail_ids: filteredFileNames(get(value, 'images')),
            };
          } else {
            data = {
              board_number: modalInfo!.board_number,
              subject: modalInfo!.subject,
              subcategory_id: modalInfo!.subcategory,
              title: get(value, 'title'),
              subtitle: modalInfo!.subtitle,
              is_example: modalInfo!.is_example,
              exercise_button: modalInfo!.exercise_button,
              mentoring_field: modalInfo!.mentoring_field,
              board_body: [
                {
                  id: get(record, 'body_list[0].id'),
                  body: get(value, 'body'),
                },
              ],
              file_ids: filteredFileNames(get(value, 'files')),
              thumbnail_ids: filteredFileNames(get(value, 'images')),
            };
          }

          isEdit === true ? await updateBoard(get(record, 'id'), data) : await createBoard(data);
          history.push({
            pathname: pathName,
            state: {
              notification: {
                type: ENotificationType.Success,
                message: isEdit === true ? '게시글이 수정되었습니다.' : '게시글이 생성되었습니다.',
              },
            },
          });
        } catch (e) {
          message.error('필수 사항들을 입력해주세요.');
          handleFieldError(e, form);
          throw e;
        } finally {
          dispatch(resetState());
          setSubmitButtonDisabled(false);
        }
      }
    }

    function onClickSubmitExample() {
      form
        .validateFields()
        .then((value) => {
          handleUpdate(value);
        })
        .catch((errorInfo) => {
          message.error('필수 사항들을 입력해주세요.');
          moveToFirstError(form, errorInfo.errorFields);
        });
      async function handleUpdate(value: any) {
        try {
          setSubmitButtonDisabled(true);
          let board: any;
          let secondBody = get(record, 'body_list[1].id');
          if (modalInfo.exercise_button === 'middle' && secondBody) {
            board = [
              { id: get(record, 'body_list[0].id'), body: get(value, 'body') },
              {
                id: get(record, 'body_list[1].id'),
                body: get(value, 'bottomBody'),
              },
            ];
          } else if (get(record, 'exercise_button') === 'middle' && secondBody) {
            board = [
              { id: get(record, 'body_list[0].id'), body: get(value, 'body') },
              {
                id: get(record, 'body_list[1].id'),
                body: get(value, 'bottomBody'),
              },
            ];
          } else if (modalInfo.exercise_button === 'middle' && !secondBody) {
            board = [
              { id: get(record, 'body_list[0].id'), body: get(value, 'body') },
              {
                id: null,
                body: get(value, 'bottomBody'),
              },
            ];
          } else {
            board = [{ id: get(record, 'body_list[0].id'), body: get(value, 'body') }];
          }
          let data;

          if (!modalInfo.subject) {
            data = {
              subject: get(record, 'subject'),
              subcategory_id: get(record, 'subcategory.id'),
              title: get(value, 'title'),
              subtitle: get(record, 'subtitle'),
              is_example: get(record, 'is_example'),
              exercise_button: get(record, 'exercise_button'),
              mentoring_field: get(record, 'mentoring_field'),
              board_body: board,
              file_ids: filteredFileNames(get(value, 'files')),
              thumbnail_ids: filteredFileNames(get(value, 'images')),
            };
          } else {
            data = {
              subject: modalInfo!.subject,
              subcategory_id: modalInfo!.subcategory,
              title: get(value, 'title'),
              subtitle: modalInfo!.subtitle,
              is_example: modalInfo!.is_example,
              exercise_button: modalInfo!.exercise_button,
              mentoring_field: modalInfo!.mentoring_field,
              board_body: board,
              file_ids: filteredFileNames(get(value, 'files')),
              thumbnail_ids: filteredFileNames(get(value, 'images')),
            };
          }

          isEdit === true ? await updateBoard(get(record, 'id'), data) : await createBoard(data);
          history.push({
            pathname: pathName,
            state: {
              notification: {
                type: ENotificationType.Success,
                message: isEdit === true ? '게시글이 수정되었습니다.' : '게시글이 생성되었습니다.',
              },
            },
          });
        } catch (e) {
          message.error('필수 사항들을 입력해주세요.');
          handleFieldError(e, form);
          throw e;
        } finally {
          dispatch(resetState());
          setSubmitButtonDisabled(false);
        }
      }
    }

    return (
      <>
        <EditorHeader record={record} />

        <Form form={form} className='body-edit'>
          <Form.Item noStyle>
            <Input.Group>
              <Row>
                <Col flex='auto'>
                  <Form.Item name='title' rules={[{ required: true, message: '제목을 입력해주세요' }]}>
                    <Input size='large' placeholder='제목' />
                  </Form.Item>
                </Col>
              </Row>
            </Input.Group>
          </Form.Item>
          <Form.Item name='body' rules={[{ required: true, message: '내용을 입력해주세요' }]}>
            <EditorComponent content={get(record, 'body_list[0].body', '')} />
          </Form.Item>

          {(modalInfo.exercise_button === 'middle' || get(record, 'exercise_button') === 'middle') &&
            modalInfo.exercise_button !== 'top' &&
            modalInfo.exercise_button !== 'bottom' && (
              <Form.Item name='bottomBody' rules={[{ required: true, message: '내용을 입력해주세요' }]}>
                <EditorComponent content={get(record, 'body_list[1].body', '')} />
              </Form.Item>
            )}

          <Form.Item
            noStyle
            name='images'
            shouldUpdate={(prevValues, currentValues) => prevValues.images !== currentValues.images}
            valuePropName='fileList'
            getValueFromEvent={(e: UploadChangeParam) => (Array.isArray(e) ? e : e && e?.fileList)}
            rules={[{ required: true, message: '내용을 입력해주세요' }]}
          >
            <Upload
              name='file'
              {...CUploadFileProps}
              action={`${Configs.API_HOST}/board/image`}
              onChange={(info: UploadChangeParam) => {
                const { status } = info.file;
                if (status === 'done') {
                  message.success(`${info.file.name} 업로드에 성공했습니다.`);
                } else if (status === 'error') {
                  message.error(`${info.file.name} 업로드에 실패했습니다.`);
                }
              }}
            >
              <Button icon={<UploadOutlined />}>썸네일용 이미지 업로드</Button>
            </Upload>
          </Form.Item>
          {/* )} */}
        </Form>

        <Divider />
        <Row justify='space-between' className='btn-bottom'>
          <Col>
            <Link to={pathName}>
              <Button type='primary' size='large'>
                목록
              </Button>
            </Link>
          </Col>
          <Col>
            <Button
              type='primary'
              size='large'
              loading={submitButtonDisabled}
              onClick={
                (modalInfo.exercise_button === 'middle' || get(record, 'exercise_button') === 'middle') &&
                modalInfo.exercise_button !== 'top' &&
                modalInfo.exercise_button !== 'bottom'
                  ? onClickSubmitExample
                  : onClickSubmit
              }
            >
              등록
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <BoardDetailWrapper>
      {(operation === EOperation.Edit && <>{adminPermission && <BoardEdit isEdit={true} />}</>) ||
        (operation === EOperation.Create && <>{adminPermission && <BoardEdit />}</>) || <NoMatch />}
    </BoardDetailWrapper>
  );
};

export default LearningBoardEditor;

export const BoardDetailWrapper = styled.div`
  color: #000;
  padding-top: 40px;
  background: #fff;
  max-width: ${CWindowWidth - 500}px;
  margin: auto;

  .ant-divider.no-margin {
    margin: 0px !important;
  }
  .box-title {
    border-right: 1px solid #f0f0f0;
    border-left: 1px solid #f0f0f0;
    border-top: 1px solid #f0f0f0;
    background-color: #fff;
    padding: 15px 20px;
    margin: 0px !important;
  }
  .box-sub-title {
    border-right: 1px solid #f0f0f0;
    border-left: 1px solid #f0f0f0;
    padding: 5px 20px;
    .label,
    .value {
      font-size: 12px;
    }
  }

  .title-item {
    margin: 0px;
    word-wrap: break-word;
    word-break: break-all;
  }

  .label {
    font-weight: bold;
    margin-right: 10px;
    font-size: 14px;
  }
  .value {
    font-weight: normal;
    font-size: 14px;
  }

  .body-edit {
    border-right: 1px solid #f0f0f0;
    border-left: 1px solid #f0f0f0;
    min-height: ${CDefaultEditorHeight}px;
    background-color: #f5f5f5;
    padding: 40px 20px;
    .tox-tinymce {
      z-index: 0;
    }
    margin-bottom: 10px;
  }

  .body-view {
    border-right: 1px solid #f0f0f0;
    border-left: 1px solid #f0f0f0;
    min-height: ${CDefaultEditorHeight}px;
    padding: 40px 20px;
    overflow-x: hidden;
    .tox-tinymce {
      border: none;
      z-index: 0;
    }
    .tox-editor-header {
      display: none;
    }
    img {
      max-width: 100%;
      height: auto;
    }
  }

  .btn-bottom {
    padding: 20px 0px;
    @media screen and ${device.md} {
      padding: 5px;
    }
  }

  .box-attachments {
    font-weight: bold;
    background-color: #f5f5f5;
    padding: 15px 20px;

    .file-name {
      color: ${PrimaryColor};
      font-weight: normal;
      display: block;
    }
  }
`;
